/**
 * Notification Component - Displays a single notification
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { markAsRead, deleteNotification, NOTIFICATION_TYPES } from '../services/notificationService';
import { acceptFriendRequest, declineFriendRequest } from '../services/friendService';
import { ref, get } from 'firebase/database';
import { database } from '../services/firebase';
import '../styles/Notification.css';

// Helper function to safely get a string value (prevents rendering objects)
const safeString = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
};

const Notification = ({ notification, userId, onDismiss, currentUser }) => {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  const [isProcessingFriendRequest, setIsProcessingFriendRequest] = useState(false);

  // Auto-dismiss read notifications after 10 seconds
  useEffect(() => {
    if (notification.read) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [notification.read]);

  const handleClick = async () => {
    // Mark as read if not already
    if (!notification.read) {
      await markAsRead(userId, notification.id);
    }

    // Navigate to action URL if provided
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleDismiss = async (e) => {
    if (e) {
      e.stopPropagation();
    }

    setIsExiting(true);

    // Wait for exit animation to complete
    setTimeout(async () => {
      await deleteNotification(userId, notification.id);
      if (onDismiss) {
        onDismiss(notification.id);
      }
    }, 300);
  };

  const handleMarkAsRead = async (e) => {
    e.stopPropagation();
    if (!notification.read) {
      await markAsRead(userId, notification.id);
    }
  };

  const handleAcceptFriendRequest = async (e) => {
    e.stopPropagation();
    if (isProcessingFriendRequest) return;

    setIsProcessingFriendRequest(true);
    try {
      const fromUserId = notification.data?.fromUserId;
      if (!fromUserId || !currentUser) {
        throw new Error('Missing user information');
      }

      // Fetch the actual friend request data to get complete information
      const requestRef = ref(database, `friendRequests/${userId}/${fromUserId}`);
      const requestSnapshot = await get(requestRef);

      if (!requestSnapshot.exists()) {
        throw new Error('Friend request no longer exists');
      }

      const requestData = requestSnapshot.val();

      await acceptFriendRequest(
        userId,
        currentUser.displayName,
        currentUser.photoURL,
        fromUserId,
        requestData.fromUserName,
        requestData.fromUserPhoto
      );

      // Dismiss the notification after accepting
      await handleDismiss(e);
    } catch (error) {
      console.error('Error accepting friend request:', error);
      if (error.message === 'Friend request no longer exists') {
        alert('This friend request has already been handled.');
        await handleDismiss(e);
      } else {
        // Show the actual error message to help debug
        const errorMsg = error.message || 'Failed to accept friend request. Please try again.';
        alert(`Error: ${errorMsg}`);
      }
    } finally {
      setIsProcessingFriendRequest(false);
    }
  };

  const handleDeclineFriendRequest = async (e) => {
    e.stopPropagation();
    if (isProcessingFriendRequest) return;

    setIsProcessingFriendRequest(true);
    try {
      const fromUserId = notification.data?.fromUserId;
      if (!fromUserId) {
        throw new Error('Missing user information');
      }

      await declineFriendRequest(userId, fromUserId);

      // Dismiss the notification after declining
      await handleDismiss(e);
    } catch (error) {
      console.error('Error declining friend request:', error);
      alert('Failed to decline friend request. Please try again.');
    } finally {
      setIsProcessingFriendRequest(false);
    }
  };

  const getNotificationIcon = () => {
    switch (notification.type) {
      case NOTIFICATION_TYPES.PLAYER_JOINED:
        return '👥';
      case NOTIFICATION_TYPES.PLAYER_LEFT:
        return '👋';
      case NOTIFICATION_TYPES.PLAYER_READY:
        return '✅';
      case NOTIFICATION_TYPES.GAME_SELECTED:
        return '🎮';
      case NOTIFICATION_TYPES.GAME_STARTED:
        return '🚀';
      case NOTIFICATION_TYPES.YOUR_TURN:
        return '⏰';
      case NOTIFICATION_TYPES.GAME_ENDED:
        return notification.data?.result === 'won' ? '🏆' : notification.data?.result === 'draw' ? '🤝' : '🎯';
      case NOTIFICATION_TYPES.FRIEND_REQUEST:
        return '👤';
      case NOTIFICATION_TYPES.MESSAGE_RECEIVED:
        return '💬';
      case NOTIFICATION_TYPES.ROOM_INVITE:
        return '📨';
      case NOTIFICATION_TYPES.SYSTEM:
        return 'ℹ️';
      default:
        return '🔔';
    }
  };

  const getNotificationClass = () => {
    let className = 'notification';
    if (!notification.read) {
      className += ' notification-unread';
    }
    if (isExiting) {
      className += ' notification-exiting';
    }

    // Add type-specific class for styling
    className += ` notification-${notification.type}`;

    return className;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const now = Date.now();
    const diff = now - timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else if (days < 7) {
      return `${days}d ago`;
    } else {
      return new Date(timestamp).toLocaleDateString();
    }
  };

  return (
    <div className={getNotificationClass()} onClick={handleClick}>
      <div className="notification-icon">
        {getNotificationIcon()}
      </div>

      <div className="notification-content">
        <div className="notification-header">
          <h4 className="notification-title">{safeString(notification?.title, 'Notification')}</h4>
          <span className="notification-time">{formatTimestamp(notification?.timestamp)}</span>
        </div>

        <p className="notification-message">{safeString(notification?.message, '')}</p>

        {!notification.read && (
          <div className="notification-unread-indicator" title="Unread notification"></div>
        )}
      </div>

      <div className="notification-actions">
        {notification.type === NOTIFICATION_TYPES.FRIEND_REQUEST && notification.message?.includes('sent you a friend request') ? (
          // Friend request actions
          <div className="friend-request-actions-inline">
            <button
              className="notification-action-btn accept-friend-btn"
              onClick={handleAcceptFriendRequest}
              disabled={isProcessingFriendRequest}
              title="Accept friend request"
            >
              {isProcessingFriendRequest ? '...' : '✓'}
            </button>
            <button
              className="notification-action-btn decline-friend-btn"
              onClick={handleDeclineFriendRequest}
              disabled={isProcessingFriendRequest}
              title="Decline friend request"
            >
              {isProcessingFriendRequest ? '...' : '✕'}
            </button>
          </div>
        ) : (
          // Regular notification actions
          <>
            {!notification.read && (
              <button
                className="notification-action-btn mark-read-btn"
                onClick={handleMarkAsRead}
                title="Mark as read"
              >
                ✓
              </button>
            )}

            <button
              className="notification-action-btn dismiss-btn"
              onClick={handleDismiss}
              title="Dismiss"
            >
              ✕
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Notification;
